import React from 'react';

const ArrayFieldSection = ({ 
  title, 
  field, 
  items = [], 
  placeholder,
  required = false,
  onArrayFieldChange,
  onAddArrayField,
  onRemoveArrayField
}) => {
  console.log(`📋 ArrayFieldSection for ${field}:`, items);

  const handleChange = React.useCallback((index, value) => {
    console.log(`📝 Changing ${field}[${index}]:`, value);
    // ✅ FIXED: Trim mat karo, value as-is bhejo
    onArrayFieldChange(field, index, value);
  }, [field, onArrayFieldChange]);

  const handleAdd = React.useCallback(() => {
    console.log(`➕ Adding new item to ${field}`);
    onAddArrayField(field);
  }, [field, onAddArrayField]);

  const handleRemove = React.useCallback((index) => {
    console.log(`➖ Removing ${field}[${index}]`);
    onRemoveArrayField(field, index);
  }, [field, onRemoveArrayField]);

  const handleBlur = React.useCallback((index, value) => {
    // ✅ FIXED: Sirf whitespace check karo, trim mat karo jo text change kare
    if (value.trim() !== value) {
      console.log(`⚠️ Value has extra whitespace, but preserving text as-is`);
      // Text mein space ho sakta hai, hum trim nahi karenge
      // Agar zaroori ho to sirf whitespace remove karo, text nahi badlo
      const trimmedValue = value.replace(/^\s+|\s+$/g, '');
      onArrayFieldChange(field, index, trimmedValue);
    }
  }, [field, onArrayFieldChange]);

  const handleKeyDown = React.useCallback((e) => {
    // ✅ FIXED: Enter key dabane se array split nahi hoga
    if (e.key === 'Enter' && !e.shiftKey) {
      // Shift+Enter se line break ho ga, Enter se nahi
      return; // Allow normal behavior
    }
  }, []);

  const safeItems = Array.isArray(items) ? items : [];

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-3">
        {title} {required && '*'}
        <span className="ml-2 text-sm font-normal text-gray-500">
          ({safeItems.length} item{safeItems.length !== 1 ? 's' : ''})
        </span>
      </label>
      <div className="space-y-3">
        {safeItems.length === 0 ? (
          <div className="text-center py-4 border border-dashed border-gray-300 rounded-xl">
            <p className="text-gray-500">No {placeholder.toLowerCase()}s added yet</p>
            <button
              type="button"
              onClick={handleAdd}
              className="mt-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-300"
            >
              Add First {placeholder}
            </button>
          </div>
        ) : (
          safeItems.map((item, index) => (
            <div key={`${field}-${index}`} className="flex items-start space-x-3">
              <textarea
                value={typeof item === 'string' ? item : String(item)}
                onChange={(e) => handleChange(index, e.target.value)}
                onBlur={(e) => handleBlur(index, e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`${placeholder} ${index + 1}`}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 resize-y min-h-[100px] whitespace-pre-wrap"
                required={required && index === 0}
                rows={4}
                style={{ lineHeight: '1.6' }}
              />
              {safeItems.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-all duration-300 mt-1"
                  title="Remove this item"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))
        )}
      </div>
      {safeItems.length > 0 && (
        <button
          type="button"
          onClick={handleAdd}
          className="mt-3 px-4 py-2 text-green-600 border border-green-300 rounded-xl hover:bg-green-50 transition-all duration-300 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Add Another {placeholder}</span>
        </button>
      )}
    </div>
  );
};

const ContentStep = ({ courseData, onArrayFieldChange, onAddArrayField, onRemoveArrayField, isEditMode }) => {
  console.log('🎯 ContentStep - Course Data:', courseData);
  console.log('🎯 ContentStep - Objectives:', courseData?.objectives);
  console.log('🎯 ContentStep - Requirements:', courseData?.requirements);
  console.log('🎯 ContentStep - Audiences:', courseData?.audiences);

  // ✅ FIXED: Ensure arrays are properly handled
  const objectives = React.useMemo(() => {
    const objs = courseData?.objectives || [];
    return Array.isArray(objs) ? objs : [];
  }, [courseData?.objectives]);
  
  const requirements = React.useMemo(() => {
    const reqs = courseData?.requirements || [];
    return Array.isArray(reqs) ? reqs : [];
  }, [courseData?.requirements]);
  
  const audiences = React.useMemo(() => {
    const auds = courseData?.audiences || [];
    return Array.isArray(auds) ? auds : [];
  }, [courseData?.audiences]);

  // ✅ FIXED: Wrapper functions to ensure proper data flow
  const handleArrayFieldChange = React.useCallback((field, index, value) => {
    console.log(`🔄 ContentStep - Changing ${field}[${index}]:`, value.substring(0, 50) + '...');
    onArrayFieldChange(field, index, value);
  }, [onArrayFieldChange]);

  const handleAddArrayField = React.useCallback((field) => {
    console.log(`➕ ContentStep - Adding new ${field}`);
    onAddArrayField(field);
  }, [onAddArrayField]);

  const handleRemoveArrayField = React.useCallback((field, index) => {
    console.log(`➖ ContentStep - Removing ${field}[${index}]`);
    onRemoveArrayField(field, index);
  }, [onRemoveArrayField]);

  return (
    <div className="p-8 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Content & Requirements</h2>
      
      <div className="space-y-8">
        <ArrayFieldSection
          title="What will students learn in your course?"
          field="objectives"
          items={objectives}
          placeholder="Learning objective"
          required={true}
          onArrayFieldChange={handleArrayFieldChange}
          onAddArrayField={handleAddArrayField}
          onRemoveArrayField={handleRemoveArrayField}
        />

        <ArrayFieldSection
          title="Requirements & Prerequisites"
          field="requirements"
          items={requirements}
          placeholder="Requirement"
          onArrayFieldChange={handleArrayFieldChange}
          onAddArrayField={handleAddArrayField}
          onRemoveArrayField={handleRemoveArrayField}
        />

        <ArrayFieldSection
          title="Who is this course for?"
          field="audiences"
          items={audiences}
          placeholder="Target audience"
          onArrayFieldChange={handleArrayFieldChange}
          onAddArrayField={handleAddArrayField}
          onRemoveArrayField={handleRemoveArrayField}
        />
      </div>

      {isEditMode && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-blue-700">
              Editing course content. Changes will be submitted for admin approval.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ContentStep);